import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue, set, onDisconnect } from 'firebase/database';
import { database } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';
import {
  BellIcon,
  UserIcon,
  Bars3Icon,
  PlusIcon,
  UserGroupIcon,
  ArrowLeftIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/AuthContext';
import defaultProfile from '../assets/default-profile.png';
import TeamInviteModal from '../components/TeamInviteModal';
import TemplatesGridInline from '../components/TemplatesGridInline';
import { getUserProfile, UserProfile } from '../utils/userService';
import AppFooter from '../components/AppFooter';
import UpgradeToProModal from '../components/UpgradeToProModal';
import { subscribePlanCounts, canCreateOrJoinTeam } from '../utils/planGate';

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

// Team member avatars with status badges
const TeamMemberAvatars: React.FC<{
  team: any;
  onlineMembers: any[];
  memberProfiles: Record<string, any>;
}> = ({ team, onlineMembers, memberProfiles }) => {
  if (!team?.members) return null;
  
  const members = Object.entries(team.members) as [string, any][];
  const onlineIds = new Set(onlineMembers.map(m => m.uid));
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-white/80 text-sm font-medium mr-2">Team:</span>
      <div className="flex -space-x-2">
        {members.slice(0, 5).map(([uid, member]) => {
          const profile = memberProfiles[uid];
          const isOnline = onlineIds.has(uid);
          const displayName = profile?.displayName || member.email?.split('@')[0] || 'User';
          const photoURL = profile?.photoURL;
          
          return (
            <div key={uid} className="relative group">
              <div className="w-10 h-10 rounded-full border-2 border-white/30 bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-sm font-semibold overflow-hidden">
                {photoURL ? (
                  <img 
                    src={photoURL} 
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  displayName.slice(0, 2).toUpperCase()
                )}
              </div>
              {/* Status badge */}
              <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                isOnline ? 'bg-green-400' : 'bg-gray-400'
              }`} />
              {/* Tooltip */}
              <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-900 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap z-10">
                {displayName} {isOnline ? '(Online)' : '(Offline)'}
              </div>
            </div>
          );
        })}
        {members.length > 5 && (
          <div className="w-10 h-10 rounded-full border-2 border-white/30 bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-semibold">
            +{members.length - 5}
          </div>
        )}
      </div>
    </div>
  );
};

const FilterPills: React.FC<{
  activeFilter: string;
  onFilterChange: (filterId: string) => void;
}> = ({ activeFilter, onFilterChange }) => {
  const filters = [
    { id: 'all', label: 'All Templates', icon: '✨', color: 'from-purple-500 to-pink-500' },
    { id: 'brainstorming', label: 'Brainstorming', icon: '💡', color: 'from-yellow-500 to-orange-500' },
    { id: 'kanban', label: 'Kanban', icon: '📋', color: 'from-blue-500 to-cyan-500' },
    { id: 'moodboard', label: 'Mood Board', icon: '🎨', color: 'from-green-500 to-teal-500' },
    { id: 'designsprint', label: 'Design Sprint', icon: '⚡', color: 'from-indigo-500 to-purple-500' }
  ];

  return (
    <>
      {filters.map((filter) => (
        <button
          key={filter.id}
          onClick={() => onFilterChange(filter.id)}
          className={`group relative overflow-hidden px-4 sm:px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
            activeFilter === filter.id
              ? `bg-gradient-to-r ${filter.color} text-white shadow-lg scale-105`
              : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-md'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">{filter.icon}</span>
            <span className="text-sm sm:text-base font-['Inter'] whitespace-nowrap">
              {filter.label}
            </span>
          </div>
          
          {/* Gradient overlay for hover effect when not active */}
          {activeFilter !== filter.id && (
            <div className={`absolute inset-0 bg-gradient-to-r ${filter.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
          )}
        </button>
      ))}
    </>
  );
};

const TeamDetailsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [team, setTeam] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [onlineMembers, setOnlineMembers] = useState<any[]>([]);
  const [onlineMemberIds, setOnlineMemberIds] = useState<string[]>([]);
  
  // Enhanced UI states
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [teamCount, setTeamCount] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);

  

  
  // Header related
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [memberProfiles, setMemberProfiles] = useState<Record<string, UserProfile | null>>({});
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  useEffect(() => {
    if (!teamId) return;
    const teamRef = ref(database, `teams/${teamId}`);
    return onValue(teamRef, (snapshot) => {
      setTeam(snapshot.val());
      setLoading(false);
    });
  }, [teamId]);

  // Subscribe to team count for gating
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = subscribePlanCounts(currentUser.uid, (c) => setTeamCount(c.teamCount));
    return () => { if (unsub) unsub(); };
  }, [currentUser?.uid]);

  // Fetch member profiles for display names
  useEffect(() => {
    if (!team?.members) return;
    const fetchProfiles = async () => {
      const entries = Object.entries(team.members) as [string, any][];
      const promises = entries.map(async ([uid]) => {
        const profile = await getUserProfile(uid);
        return [uid, profile] as [string, UserProfile | null];
      });
      const results = await Promise.all(promises);
      setMemberProfiles(Object.fromEntries(results));
    };
    fetchProfiles();
  }, [team?.members]);

  useEffect(() => {
    if (!teamId || !team?.members) return;
    
    console.log('Setting up global presence listener for team:', teamId);
    
    // Listen to global presence for all users
    const globalPresenceRef = ref(database, 'global-presence');
    const unsub = onValue(globalPresenceRef, (snap) => {
      const globalPresenceData = snap.val();
      console.log('Global presence data received:', globalPresenceData);
      
      if (globalPresenceData && team?.members) {
        const currentTime = Date.now();
        const activeThreshold = 2 * 60 * 1000; // 2 minutes
        
        const onlineTeamMembers: any[] = [];
        const onlineTeamMemberIds: string[] = [];
        
        // Check each team member's global presence
        Object.entries(team.members).forEach(([uid, member]: [string, any]) => {
          const userPresence = globalPresenceData[uid];
          
          if (userPresence && 
              userPresence.lastActive && 
              userPresence.isOnline &&
              (currentTime - userPresence.lastActive) < activeThreshold) {
            
            console.log(`Team member ${uid} is online:`, userPresence);
            onlineTeamMembers.push({
              uid,
              displayName: userPresence.displayName,
              photoURL: userPresence.photoURL,
              email: userPresence.email,
              platform: userPresence.platform,
              lastActive: userPresence.lastActive
            });
            onlineTeamMemberIds.push(uid);
          }
        });
        
        console.log('Online team members:', onlineTeamMembers);
        setOnlineMembers(onlineTeamMembers);
        setOnlineMemberIds(onlineTeamMemberIds);
      } else {
        setOnlineMembers([]);
        setOnlineMemberIds([]);
      }
    });
    
    return () => {
      unsub();
    };
  }, [teamId, team]);

  // Listen for pending invites to show in header badge
  useEffect(() => {
    if (!currentUser?.email) return;
    const invitesRef = ref(database, 'invites');
    const unsub = onValue(invitesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invitesList = Object.entries(data)
          .map(([id, invite]: [string, any]) => ({ id, ...invite }))
          .filter(invite => invite.email === currentUser.email && invite.status === 'pending');
        setPendingInvites(invitesList);
      } else {
        setPendingInvites([]);
      }
    });
    return () => unsub();
  }, [currentUser?.email]);



  const handleRoleChange = async (memberKey: string, newRole: string) => {
    if (!teamId) return;
    const memberRef = ref(database, `teams/${teamId}/members/${memberKey}/role`);
    await set(memberRef, newRole);
  };



  const handleAcceptInvite = async (invite: any) => {
    try {
      if (!canCreateOrJoinTeam(teamCount)) {
        setShowUpgrade(true);
        return;
      }
      const inviteRef = ref(database, `invites/${invite.id}`);
      await set(inviteRef, { ...invite, status: 'accepted' });
      
      // Add user to team
      const teamMemberRef = ref(database, `teams/${invite.teamId}/members/${currentUser?.uid}`);
      await set(teamMemberRef, {
        email: currentUser?.email,
        role: invite.role,
        joinedAt: Date.now(),
      });
    } catch (err: any) {
      console.error('Error accepting invite:', err);
    }
  };

  const handleDeclineInvite = async (invite: any) => {
    try {
      const inviteRef = ref(database, `invites/${invite.id}`);
      await set(inviteRef, { ...invite, status: 'declined' });
    } catch (err: any) {
      console.error('Error declining invite:', err);
    }
  };

  // Filter management
  const handleFilterChange = useCallback((filterId: string) => {
    setActiveFilter(filterId);
  }, []);

  // Enhanced FilterBar without search - pills only
  const FilterBar = () => (
    <div className="px-4 sm:px-6 py-6 bg-white border-b border-gray-100">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 font-['Poppins']">Choose a Template</h2>
          <span className="text-sm text-gray-500 font-['Inter']">Select a template to get started</span>
        </div>
        
        {/* Enhanced filter pills for mobile and desktop */}
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <FilterPills 
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-gray-500">
        <p>Team not found.</p>
        <button className="mt-4 text-blue-600 hover:underline" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  const getInitials = (name: string) => name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const heroInitials = getInitials(team.name);

  const roleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-600';
      case 'editor':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Enhanced Components
  const TeamHeader = () => (
    <div className="relative bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 rounded-b-3xl pb-8 pt-6 px-6">
      {/* Dark mode toggle and notifications */}
      <div className="absolute top-6 right-6 flex items-center gap-2">
        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => { setShowInviteDropdown(v => !v); setShowProfileDropdown(false); }}
            className="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
          >
            <BellIcon className="w-5 h-5" />
            {pendingInvites.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                {pendingInvites.length}
              </span>
            )}
          </button>
          
          {/* Invite Dropdown */}
          {showInviteDropdown && (
            <div className="absolute right-0 top-10 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-40">
              <div className="p-4 border-b font-semibold text-gray-800">Pending Invites</div>
              {pendingInvites.length === 0 ? (
                <div className="p-4 text-gray-500 text-sm">No pending invites.</div>
              ) : (
                pendingInvites.map(invite => (
                  <div key={invite.id} className="p-4 border-b last:border-b-0 flex flex-col gap-2">
                    <div className="font-medium text-gray-900">{invite.teamName}</div>
                    <div className="text-xs text-gray-500">Invited by: {invite.inviter}</div>
                    <div className="flex gap-2 mt-2">
                      <button 
                        className="flex-1 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                        onClick={async () => { await handleAcceptInvite(invite); }}
                      >
                        Accept
                      </button>
                      <button 
                        className="flex-1 px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                        onClick={async () => { await handleDeclineInvite(invite); }}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Profile dropdown */}
        <div className="relative">
          <button
            onClick={() => { setShowProfileDropdown(v => !v); setShowInviteDropdown(false); }}
            className="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
          >
            <UserIcon className="w-5 h-5" />
          </button>
          {showProfileDropdown && (
            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                onClick={() => { setShowProfileDropdown(false); navigate('/profile'); }}
              >
                Profile
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-red-600"
                onClick={async () => { setShowProfileDropdown(false); await authContext?.logout(); }}
              >
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Dark mode toggle */}
        <button
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
        >
          {isDarkMode ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
        </button>
      </div>

      {/* Back button */}
      <button
        onClick={() => navigate('/dashboard')}
        className="absolute top-6 left-6 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
      >
        <ArrowLeftIcon className="w-5 h-5" />
      </button>

      {/* Team info with avatar overlap */}
      <div className="mt-12 flex items-end">
        <div className="relative">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white text-2xl font-bold border-4 border-white/30">
            {team?.name?.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          {/* Online indicator */}
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-400 border-3 border-white rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        </div>
        
        <div className="ml-6 mb-2 flex-1">
          <h1 className="text-3xl font-bold text-white font-['Poppins']">{team?.name}</h1>
          <div className="mt-3">
            <TeamMemberAvatars 
              team={team}
              onlineMembers={onlineMembers}
              memberProfiles={memberProfiles}
            />
          </div>
        </div>
      </div>

      {/* Invite button */}
      <div className="mt-6">
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all duration-200 font-medium"
        >
          <PlusIcon className="w-5 h-5" />
          Invite Member
        </button>
      </div>
    </div>
  );

  const TemplateCard = ({ template }: { template: any }) => (
    <div 
      className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 cursor-pointer"
      onClick={() => navigate(template.path)}
    >
      {/* Icon and gradient background */}
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
        <SparklesIcon className="w-8 h-8 text-white" />
      </div>

      {/* Title and description */}
      <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Poppins']">{template.title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4 font-['Inter']">{template.description}</p>

      {/* Meta information */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <ClockIcon className="w-4 h-4" />
            {template.duration}
          </span>
          <span className="flex items-center gap-1">
            <UserGroupIcon className="w-4 h-4" />
            Up to {template.capacity}
          </span>
        </div>
        <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">
          {template.category}
        </span>
      </div>
    </div>
  );

  const TemplateGrid = () => {
    const templates = [
      {
        id: 1,
        title: 'Brainstorming Board',
        description: 'Generate and organize ideas with mind maps, sticky notes, and visual thinking tools.',
        duration: '30-60 min',
        capacity: 10,
        category: 'Collaboration',
        icon: '💡',
        path: `/teams/${teamId}/brainstorming`
      },
      {
        id: 2,
        title: 'Kanban Board',
        description: 'Manage tasks and workflows with drag-and-drop columns and progress tracking.',
        duration: '15-45 min',
        capacity: 15,
        category: 'Planning',
        icon: '📋',
        path: `/teams/${teamId}/kanban`
      },
      {
        id: 3,
        title: 'Mood Board',
        description: 'Create visual inspiration boards with images, colors, and design elements.',
        duration: '20-40 min',
        capacity: 8,
        category: 'Design',
        icon: '🎨',
        path: `/teams/${teamId}/moodboards-v3/new`
      },
      {
        id: 4,
        title: 'Design Sprint',
        description: 'Structured design process with user research, ideation, and prototyping phases.',
        duration: '2-5 days',
        capacity: 8,
        category: 'Design',
        icon: '⚡',
        path: `/teams/${teamId}/design-sprints`
      }
    ];

    const filteredTemplates = templates.filter(template => {
      // Filter by active filter only (no search)
      if (activeFilter === 'all') {
        return true;
      } else if (activeFilter === 'brainstorming') {
        return template.title.toLowerCase().includes('brainstorming');
      } else if (activeFilter === 'kanban') {
        return template.title.toLowerCase().includes('kanban');
      } else if (activeFilter === 'moodboard') {
        return template.title.toLowerCase().includes('mood board');
      } else if (activeFilter === 'designsprint') {
        return template.title.toLowerCase().includes('design sprint');
      }
      return true;
    });

    return (
      <div className="px-6 py-8">
        {filteredTemplates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredTemplates.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-2">No templates found</div>
            <div className="text-gray-400 text-sm">
              Try selecting a different filter category
            </div>
          </div>
        )}
      </div>
    );
  };



  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      {/* Enhanced Team Header */}
      {team && <TeamHeader />}
      
      {/* Enhanced Filter Bar */}
      <FilterBar />
      


      {/* Enhanced Template Grid */}
      <TemplateGrid />

      {/* Enhanced Invite Modal */}
      <TeamInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        teamId={teamId!}
        teamName={team?.name || ''}
      />

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => setShowInviteModal(true)}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
        >
          <PlusIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Footer */}
      <AppFooter />
      {showUpgrade && (
        <UpgradeToProModal isOpen={true} onClose={() => setShowUpgrade(false)} context="team" />
      )}
    </div>
  );
};

export default TeamDetailsPage; 